import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Edit3, Trash2, PlusCircle, Package, Clock, Calculator, MapPin, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { usePsStore } from "../../store/psStore";
import { metaFor } from "../../components/ServiceMeta";
import { chf } from "../../lib/format";

const DELIVERY_LABELS = {
  MORNING_STANDARD: "Fin de matinée", AFTERNOON_STANDARD: "Fin d'après-midi",
  EXPRESS: "Express", ONE_HOUR: "Dans 1 heure", SCHEDULED: "Horaire choisi",
  AFTER_HOURS: "Livraison tardive", NIGHT: "Nuit",
};

export default function PsCart() {
  const nav = useNavigate();
  const { order, quote, removeService, refresh, reset, setPricingAddress } = usePsStore();
  const [addr, setAddr] = useState("");
  const [quoting, setQuoting] = useState(false);

  useEffect(() => { refresh().catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const subs = order?.sub_orders || [];
  const stopCount = order?.stop_count ?? 0;
  const itemCount = order?.item_count ?? 0;
  const maxStops = order?.max_stops ?? 5;

  const submitAddress = async () => {
    const query = addr.trim();
    if (!query || quoting) return;
    setQuoting(true);
    try { await setPricingAddress({ address: query }); toast.success("Adresse enregistrée"); }
    catch (e) { const d = e?.response?.data?.detail; toast.error((typeof d === "string" ? d : d?.message) || "Adresse introuvable. Précisez la rue et le numéro."); }
    finally { setQuoting(false); }
  };

  const confirmRemove = async (stop, label, count) => {
    if (count > 0 && !window.confirm(`Supprimer ${label} ? Les ${count} article(s) seront retirés.`)) return;
    try { await removeService(stop); } catch { toast.error("Impossible de supprimer"); }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      <div className="flex items-center gap-3 mb-4">
        <button onClick={() => nav("/personal-shopper")} data-testid="ps-cart-back" style={{ color: "#0b1220" }}><ArrowLeft size={24} /></button>
        <h1 className="text-xl font-extrabold" style={{ color: "#0b1220" }}>Mon panier</h1>
      </div>

      {order && <p className="text-xs font-semibold mb-1" style={{ color: "#64748b" }}>Commande #{order.order_number}</p>}
      <div className="flex items-center gap-2.5 mb-5">
        <span className="font-extrabold" style={{ color: "#0b1220" }}>{stopCount} arrêt{stopCount > 1 ? "s" : ""}</span>
        <span className="w-1 h-1 rounded-full" style={{ background: "#cbd5e1" }} />
        <span className="font-extrabold" style={{ color: "#0b1220" }}>{itemCount} article{itemCount > 1 ? "s" : ""}</span>
      </div>

      {subs.length === 0 ? (
        <div className="text-center py-14" style={{ color: "#64748b" }}>
          <Package size={44} className="mx-auto mb-3" style={{ color: "#cbd5e1" }} /> Votre panier est vide.
        </div>
      ) : subs.map((sub) => {
        const m = metaFor(sub.stop_type, sub.store_name);
        const Icon = m.Icon;
        return (
          <div key={sub.id} className="bg-white rounded-2xl p-4 mb-3" style={{ border: "1px solid #f0f0f0" }} data-testid={`ps-cart-${sub.stop_type}`}>
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: m.accent + "1A" }}><Icon size={22} color={m.accent} /></div>
              <div className="flex-1">
                <p className="font-extrabold" style={{ color: "#0b1220" }}>{sub.store_name}</p>
                <p className="text-xs mt-0.5" style={{ color: "#64748b" }}>{sub.item_count} {sub.fulfillment_type === "MERCHANT_PREPARED" ? "demande(s)" : "article(s)"}</p>
              </div>
            </div>
            <div className="flex gap-2.5 mt-3">
              <button onClick={() => nav(`/personal-shopper/${sub.stop_type}`)} data-testid={`ps-cart-edit-${sub.stop_type}`}
                className="flex-1 rounded-lg py-2.5 text-sm font-bold flex items-center justify-center gap-1.5" style={{ background: "#f1f5f9", color: "#0b1220" }}><Edit3 size={16} /> Modifier</button>
              <button onClick={() => confirmRemove(sub.stop_type, sub.store_name, sub.item_count)} data-testid={`ps-cart-remove-${sub.stop_type}`}
                className="flex-1 rounded-lg py-2.5 text-sm font-bold flex items-center justify-center gap-1.5" style={{ background: "#fef2f2", color: "#dc2626" }}><Trash2 size={16} /> Supprimer</button>
            </div>
          </div>
        );
      })}

      {stopCount < maxStops && (
        <button onClick={() => nav("/personal-shopper")} data-testid="ps-cart-add-service"
          className="w-full rounded-2xl py-4 mb-4 flex items-center justify-center gap-2 font-extrabold"
          style={{ border: "1.5px dashed #16a34a", color: "#16a34a" }}><PlusCircle size={20} /> Ajouter un service</button>
      )}

      {subs.length > 0 && (
        <>
          <div className="flex items-center gap-2 rounded-xl p-3.5 mb-3" style={{ background: "#e0f2fe" }}>
            <Package size={18} color="#0369a1" /><span className="text-sm" style={{ color: "#075985" }}>Vos différents retraits seront regroupés avant votre livraison.</span>
          </div>

          <button onClick={() => nav("/personal-shopper/livraison")} data-testid="ps-cart-delivery"
            className="w-full flex items-center gap-3 bg-white rounded-2xl p-4 mb-3" style={{ border: "1px solid #dcfce7" }}>
            <Clock size={20} color="#16a34a" />
            <div className="flex-1 text-left">
              <p className="text-xs" style={{ color: "#64748b" }}>Livraison</p>
              <p className="font-extrabold" style={{ color: "#0b1220" }}>{DELIVERY_LABELS[order?.delivery_mode] || "Choisir un créneau"}</p>
            </div>
            <span className="text-xs font-extrabold" style={{ color: "#16a34a" }}>{order?.delivery_mode ? "MODIFIER" : "CHOISIR"}</span>
          </button>

          <div className="bg-white rounded-2xl p-4 mb-4" style={{ border: "1px solid #f0f0f0" }} data-testid="ps-quote-card">
            <p className="font-extrabold mb-3" style={{ color: "#0b1220" }}>Détail des frais</p>
            <div className="flex gap-2 mb-1.5">
              <input value={addr} onChange={(e) => setAddr(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submitAddress()}
                placeholder="Adresse de livraison (rue, n°, NPA)" data-testid="ps-quote-address-input"
                className="flex-1 rounded-lg px-3 py-2.5 text-sm outline-none" style={{ background: "#f8fafc", border: "1px solid #e2e8f0", color: "#0b1220" }} />
              <button onClick={submitAddress} disabled={quoting} data-testid="ps-quote-address-submit"
                className="w-11 rounded-lg flex items-center justify-center text-white" style={{ background: "#16a34a" }}><Calculator size={18} /></button>
            </div>
            {order?.pricing_address && (
              <p className="text-xs mb-1.5 flex items-center gap-1" style={{ color: "#16a34a" }} data-testid="ps-quote-address-resolved"><MapPin size={12} /> {order.pricing_address}</p>
            )}
            {(quote?.lines || []).map((l) => (
              <div key={l.key} className="flex items-center py-2" style={{ borderTop: "1px solid #f1f5f9" }} data-testid={`ps-quote-line-${l.key}`}>
                <div className="flex-1">
                  <p className="text-sm font-semibold" style={{ color: "#0b1220" }}>{l.label}</p>
                  {l.detail && <p className="text-xs" style={{ color: "#94a3b8" }}>{l.detail}</p>}
                </div>
                <span className="text-sm font-bold" style={{ color: l.status === "PENDING" ? "#f59e0b" : "#0b1220" }}>{l.amount_chf == null ? "—" : chf(l.amount_chf)}</span>
              </div>
            ))}
            <div className="flex items-center justify-between mt-3 pt-3" style={{ borderTop: "2px solid #0b1220" }}>
              <span className="text-sm font-extrabold" style={{ color: "#0b1220" }}>FRAIS PERSONAL SHOPPER</span>
              <span className="text-xl font-extrabold" style={{ color: "#16a34a" }} data-testid="ps-quote-total">{quote ? chf(quote.shopper_fee_chf) : "—"}</span>
            </div>
            {quote?.distance_pending && (
              <p className="text-xs mt-2" style={{ color: "#f59e0b" }}>
                {order?.pricing_address ? "Distance calculée au moment de la livraison." : "Ajoutez votre adresse pour inclure la distance."}
              </p>
            )}
            <p className="text-xs mt-1.5" style={{ color: "#94a3b8" }}>Marchandise facturée séparément lors du paiement.</p>
            <p className="text-xs mt-1.5 leading-relaxed" style={{ color: "#1e40af" }} data-testid="ps-cart-trunk-note">
              🚗 Votre commande vous attend à la voiture (retrait au coffre). Remise à la porte en option (2.90 CHF, 3.90 dès le 2ᵉ étage).
            </p>
          </div>

          <button onClick={() => nav("/personal-shopper/checkout")} data-testid="ps-cart-checkout"
            className="w-full rounded-2xl font-extrabold text-white py-4 flex items-center justify-center gap-2" style={{ background: "#0b1220" }}>
            Vérifier ma commande <ArrowRight size={18} />
          </button>
          <button onClick={async () => { if (window.confirm("Vider le panier ? Tous les services et articles seront retirés.")) { await reset(); nav("/personal-shopper"); } }}
            className="w-full text-center py-4 text-sm font-semibold" style={{ color: "#94a3b8" }} data-testid="ps-cart-clear">Vider le panier</button>
        </>
      )}
    </div>
  );
}
